"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Flame, Zap, Waves, Cylinder, ArrowRight, CircleCheck as CheckCircle2, ShieldAlert } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface HeaterTypeItem {
  id: string;
  title: string;
  badge: string;
  description: string;
  feature1: string;
  feature2: string;
  linkTo: string;
  linkLabel: string;
  icon: "tank" | "gas" | "electric" | "tankless";
}
const heaterData: HeaterTypeItem[] = [{
  id: "tank",
  title: "Traditional Tank",
  badge: "Standard Storage",
  description: "Centralized storage systems delivering steady hot water reservoirs for standard household demand.",
  feature1: "30 to 80 gallon storage",
  feature2: "Cost-effective replacement",
  linkTo: "/water-heater-repair/",
  linkLabel: "View Tank Services",
  icon: "tank"
}, {
  id: "gas",
  title: "Gas Water Heaters",
  badge: "Rapid Recovery",
  description: "Natural gas or propane-fueled heating setups built for fast water reheating and heavy daily use.",
  feature1: "Quick temperature recovery",
  feature2: "Lower operating costs",
  linkTo: "#",
  linkLabel: "Explore Gas Units",
  icon: "gas"
}, {
  id: "electric",
  title: "Electric Water Heaters",
  badge: "High Efficiency",
  description: "Safe electric dual-element setups ideal for homes without gas lines, delivering reliable heat.",
  feature1: "Zero fuel emissions",
  feature2: "Flexible utility placement",
  linkTo: "#",
  linkLabel: "Explore Electric Units",
  icon: "electric"
}, {
  id: "tankless",
  title: "Tankless Water Heaters",
  badge: "On-Demand Flow",
  description: "Compact, on-demand continuous flow units that eliminate bulky tanks and reduce standby energy losses.",
  feature1: "Endless hot water supply",
  feature2: "Space-saving wall design",
  linkTo: "/tankless-water-heater-service/",
  linkLabel: "View Tankless Services",
  icon: "tankless"
}];
export default function WaterHeaters() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (type: HeaterTypeItem["icon"]) => {
    switch (type) {
      case "tank":
        return <Cylinder className="size-6 text-primary" />;
      case "gas":
        return <Flame className="size-6 text-accent" />;
      case "electric":
        return <Zap className="size-6 text-chart-3" />;
      case "tankless":
        return <Waves className="size-6 text-primary" />;
      default:
        return <Cylinder className="size-6 text-primary" />;
    }
  };
  return <section id="waterheatertypes" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            System Types We Service & Install
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Complete Care for Every Water Heater Type
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            From emergency repairs to scheduled replacements, our licensed Jonesboro technicians service all primary heating configurations with rapid local response.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {heaterData.map((item, index) => {
          const hasExternalLink = item.linkTo && item.linkTo !== "#";
          return <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.4,
            delay: shouldReduceMotion ? 0 : index * 0.1
          }} className="h-full flex">
                <Card className="flex-1 flex flex-col justify-between border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] bg-card hover:border-border/80">
                  <div>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-2 mb-4">
                        <div className="size-12 rounded-xl bg-secondary/70 flex items-center justify-center border border-border/60">
                          {getIcon(item.icon)}
                        </div>
                        <Badge variant="outline" className="text-[11px] font-medium text-muted-foreground border-border">
                          {item.badge}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-default font-bold text-card-foreground">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm pt-2 leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-0 pb-6">
                      <ul className="space-y-2 border-t border-border/60 pt-4">
                        <li className="flex items-center gap-2 text-xs font-medium text-foreground">
                          <CheckCircle2 className="size-4 text-accent shrink-0" />
                          <span>{item.feature1}</span>
                        </li>
                        <li className="flex items-center gap-2 text-xs font-medium text-foreground">
                          <CheckCircle2 className="size-4 text-accent shrink-0" />
                          <span>{item.feature2}</span>
                        </li>
                      </ul>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-0">
                    {hasExternalLink ? <Button asChild variant="outline" size="sm" className="w-full justify-between group">
                        <Link to={item.linkTo}>
                          <span>{item.linkLabel}</span>
                          <ArrowRight className="size-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                      </Button> : <Button asChild variant="outline" size="sm" className="w-full justify-between group">
                        <Link to="/water-heater-repair/">
                          <span>{item.linkLabel}</span>
                          <ArrowRight className="size-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                      </Button>}
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="mt-12 bg-secondary/50 rounded-2xl border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-start sm:items-center gap-4">
            <div className="size-10 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
              <ShieldAlert className="size-5" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-foreground">
                Not sure which water heater system you have?
              </h3>
              <p className="text-sm text-muted-foreground">
                Our technicians can inspect your existing setup and advise the most cost-effective repair or replacement.
              </p>
            </div>
          </div>
          <Button asChild size="lg" className="w-full sm:w-auto shrink-0">
            <Link to="/contact/">
              Request System Inspection
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}